import { memo, useCallback, useMemo } from 'react'

import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { AvcSimpleAudioInputConfig, AvcAudioInputConfig } from '@nativewaves/nw-cloud-sdk/content'
import { useQueryClient } from '@tanstack/react-query'
import { omit } from 'lodash-es'
import { Controller, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { InputErrorEndAdornment } from '@shared/components/ErrorHandling'
import { DialogFormContent } from '@shared/layouts'
import {
  FormStructure as AvcTaskFormStructure,
  useMeaningfulDefaultName,
} from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Task/Config'
import { Item as SourceItem } from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Task/Config/Sources'
import { useNanoID } from 'hooks/utils'
import { AdvancedSettings } from 'layouts'
import { avcTaskConfigRoute } from 'pages/NWPlatform/Workspaces/Workspace/AvcTasks/AvcTask'
import { avcTaskQueryKeys } from 'services/queryKeys'
import { stopPropagate } from 'utils'

type FormStructure = AvcSimpleAudioInputConfig & {
  enabled?: boolean
  name?: string | null
}

type SimpleDialogContentProps = {
  onSubmit: (data: AvcAudioInputConfig) => void
  inputIdx?: number
}

const SimpleRaw = ({ onSubmit, inputIdx }: SimpleDialogContentProps) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()
  const generatedId = useNanoID()

  const { avcTaskId: taskId } = avcTaskConfigRoute.useParams()

  const { getValues, watch: watchAvcTask } = useFormContext<AvcTaskFormStructure>()

  const selectedAudioInput = useMemo(
    () => (inputIdx !== undefined ? getValues().config?.data.audioInputs?.at(inputIdx) : undefined),
    [getValues, inputIdx],
  )

  const meaningfulDefaultName = useMeaningfulDefaultName('audioInputs')

  const { control, handleSubmit, formState } = useForm<FormStructure>({
    defaultValues: {
      ...selectedAudioInput?.simple,
      source: selectedAudioInput?.simple?.source
        ? selectedAudioInput?.simple?.source
        : !getValues().config?.data.sources?.[0]?.disabled
          ? getValues().config?.data.sources?.[0]?.id
          : 'empty',
      name: selectedAudioInput?.name,
      enabled: !selectedAudioInput?.disabled,
    },
  })

  const handleFormSubmit = useCallback(
    async (data: FormStructure) => {
      const base = queryClient.getQueryData(avcTaskQueryKeys.detail({ taskId }).queryKey)?.config
      if (!base) {
        throw new Error(t('domain:Workspace.AvcTask.noBaseConfigFound'))
      }

      const transformedData: AvcAudioInputConfig = {
        simple: { ...omit(data, ['name', 'enabled']), index: data.index || 0 },
        type: 'Simple',
        id: selectedAudioInput?.id || generatedId,
        name: data.name || meaningfulDefaultName,
        disabled: !data.enabled,
      }

      onSubmit(transformedData)
    },
    [t, generatedId, selectedAudioInput?.id, meaningfulDefaultName, onSubmit, queryClient, taskId],
  )

  return (
    <DialogFormContent
      title="Manage Simple Audio-Input"
      onSubmit={stopPropagate(handleSubmit(handleFormSubmit))}
      Illustration={null}
      SaveButtonProps={{
        disabled: formState.isSubmitted && !formState.isValid,
      }}
    >
      <Box sx={{ mt: 4, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: 'auto', gap: 2 }}>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState, formState }) => (
            <TextField
              sx={{ gridColumn: '1 / span 2' }}
              {...field}
              label={t('name')}
              placeholder={meaningfulDefaultName}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />,
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="enabled"
          render={({ field }) => (
            <FormControlLabel
              control={<Switch {...field} checked={field.value} onChange={(_, checked) => field.onChange(checked)} />}
              label={t('domain:Workspace.AvcTask.enabled')}
              labelPlacement="start"
            />
          )}
        />

        {watchAvcTask('config.data.sources')?.length &&
        !watchAvcTask('config.data.sources')!.every((item) => item.disabled) ? (
          <>
            <Controller
              control={control}
              name="source"
              rules={{
                required: true,
              }}
              render={({ field, fieldState, formState }) => (
                <FormControl sx={{ gridColumn: '1 / span 2' }}>
                  <InputLabel id="source">{t('entity:content.AvcTask.source', { count: 1 })}</InputLabel>
                  <Select
                    {...field}
                    labelId="source"
                    input={
                      <OutlinedInput
                        label={t('entity:content.AvcTask.source', { count: 1 })}
                        endAdornment={
                          <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />
                        }
                      />
                    }
                    renderValue={(selected) => {
                      const source = watchAvcTask('config.data.sources')?.find((item) => item.id === selected)

                      if (source) {
                        return (
                          <SourceItem
                            sx={[{ my: -1 }, !!source.disabled && { color: 'action.disabled' }]}
                            source={source}
                          />
                        )
                      }
                    }}
                  >
                    {watchAvcTask('config.data.sources')?.map((source) => (
                      <MenuItem
                        sx={[!!source.disabled && { color: 'action.disabled' }]}
                        key={source.id}
                        value={source.id}
                      >
                        <SourceItem source={source} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="index"
              render={({ field, fieldState, formState }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t('domain:Workspace.AvcTask.index')}
                  placeholder={t('domain:Workspace.AvcTask.zero')}
                  type="number"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />
                    ),
                  }}
                />
              )}
            />
          </>
        ) : (
          <Typography variant="heading-sm">{t('domain:Workspace.AvcTask.sourceNotification')}</Typography>
        )}
        <AdvancedSettings sx={{ gridColumn: '1 / -1' }}>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', columnGap: 1 }}>
            <Controller
              control={control}
              name="pan"
              render={({ field, fieldState, formState }) => (
                <TextField
                  sx={{ flex: 2 }}
                  {...field}
                  size="small"
                  type="text"
                  label={t('domain:Workspace.AvcTask.pan')}
                  placeholder={t('domain:Workspace.AvcTask.exampleUnknown')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputErrorEndAdornment fieldState={fieldState} isSubmitted={formState.isSubmitted} />
                    ),
                  }}
                />
              )}
            />
          </Box>
        </AdvancedSettings>
      </Box>
    </DialogFormContent>
  )
}

export const Simple = memo(SimpleRaw)
